import { Fragment, useCallback } from 'react';
import { lt, useFeatureFlag } from '@mews/framework';
import { useLanguageCode } from '@mews-commander/core';
import { Button, Card, CardContent, Container, ExpandableContent, IconName, Stack } from '@optimus-web/core';
import { FeatureFlag } from '@mews-commander/enums';
import { DrawerChatResultContentProps } from '../../drawer/content/Content';
import { useAssistantChatStore, useAssistantChatWindowStore } from '../../store';
import { AssistantChatWindowType } from './AssistantChatWindowType';
import { AssistantChatMessage } from './AssistantChatMessageContainer';

export const AssistantMessageBox = ({
    chatResult,
    showFollowUp,
    searchTerm,
    handleUnFocused,
}: DrawerChatResultContentProps) => {
    const { setAssistantChatWindowState } = useAssistantChatWindowStore();
    const { chatMessageResult, setAssistantChatDataStore } = useAssistantChatStore();
    const assistantAdvanceSearchEnabled = useFeatureFlag(FeatureFlag.assistantAdvanceSearchEnabled);
    const messageMainContent = chatResult.responseMessage?.content;
    const languageCode = useLanguageCode();

    const handleOpenChatWindow = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            e.preventDefault();

            if (searchTerm?.trimEnd()) {
                setAssistantChatWindowState({
                    showMewsAssistant: true,
                    assistantChatWindowType: AssistantChatWindowType.sideBar,
                });

                const currentDate = new Date().toLocaleDateString(languageCode);
                const uniqueId = window.crypto.randomUUID();

                const prevChatMessageResult = { ...(chatMessageResult || {}) };
                const assistantChatMessage: AssistantChatMessage = {
                    id: uniqueId,
                    searchTerm: searchTerm.trimStart().trimEnd(),
                    createDate: currentDate,
                };
                prevChatMessageResult[currentDate] =
                    prevChatMessageResult[currentDate] && Object.keys(prevChatMessageResult[currentDate]).length > 0
                        ? {
                              ...prevChatMessageResult[currentDate],
                              [uniqueId]: { assistantChatMessage },
                          }
                        : {
                              [uniqueId]: { assistantChatMessage },
                          };

                setAssistantChatDataStore({
                    chatMessageResult: { ...prevChatMessageResult },
                });
            }

            handleUnFocused();
        },
        [
            searchTerm,
            handleUnFocused,
            setAssistantChatWindowState,
            languageCode,
            chatMessageResult,
            setAssistantChatDataStore,
        ]
    );

    return (
        <Fragment>
            {showFollowUp ? (
                <Stack distribution="stretch" vertical>
                    <Card padding="200">
                        <CardContent padding="200">
                            <ExpandableContent
                                lines={12}
                                expandedToggleText={lt('LessInfo')}
                                collapsedToggleText={lt('MoreInfo')}
                            >
                                {messageMainContent}
                            </ExpandableContent>
                        </CardContent>
                    </Card>
                    {assistantAdvanceSearchEnabled && (
                        <Container padding={['100', '200']}>
                            <Stack alignment="end">
                                <Button
                                    type="button"
                                    variant="secondary"
                                    size="small"
                                    icon={IconName.AI}
                                    iconPosition="left"
                                    onClick={handleOpenChatWindow}
                                >
                                    {lt('AssistantAskFollowUp')}
                                </Button>
                            </Stack>
                        </Container>
                    )}
                </Stack>
            ) : (
                <Stack vertical>{messageMainContent}</Stack>
            )}
        </Fragment>
    );
};
